import { Chip, Radio, RadioGroup, Spinner } from '@heroui/react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useConsultDoctorsQuery } from '@/api/health-plan';
import type { ConsultUrgency } from '@/api/model';
import { AppModal } from '@/components/common/AppModal';
import { SimpleSelect } from '@/components/common/SimpleSelect';
import { SimpleTextArea } from '@/components/common/SimpleTextArea';
import { notify } from '@/lib/feedback';
import { consultUrgencyMap } from '@/lib/health-plan-enums';

// region 类型

export interface HealthPlanConsultValues {
  consultDoctorId: string;
  urgency: ConsultUrgency;
  reason: string;
}

// endregion

export function HealthPlanConsultDialog({
  isOpen,
  onClose,
  onConfirm,
  isPending,
  coordinating = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (values: HealthPlanConsultValues) => Promise<void>;
  isPending: boolean;
  coordinating?: boolean;
}) {
  const [consultDoctorId, setConsultDoctorId] = useState('');
  const [consultUrgency, setConsultUrgency] =
    useState<ConsultUrgency>('normal');
  const [consultReason, setConsultReason] = useState('');
  const { data: consultDoctors, isLoading: consultDoctorsLoading } =
    useConsultDoctorsQuery(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    setConsultDoctorId('');
    setConsultUrgency('normal');
    setConsultReason('');
  }, [isOpen]);

  const consultDoctorOptions = (consultDoctors ?? []).map((doctor) => ({
    label: doctor.name || doctor.id,
    value: doctor.id,
  }));

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title={coordinating ? '转交专科会诊' : '发起会诊'}
      confirmText={coordinating ? '确认转交' : '提交'}
      cancelText="取消"
      onConfirm={async () => {
        if (!consultDoctorId) {
          notify.warning('请选择会诊医生');
          return;
        }
        if (!consultReason.trim()) {
          notify.warning('请填写会诊原因');
          return;
        }
        await onConfirm({
          consultDoctorId,
          urgency: consultUrgency,
          reason: consultReason.trim(),
        });
      }}
      confirmLoading={isPending}
    >
      {consultDoctorsLoading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : consultDoctorOptions.length === 0 ? (
        <div className="p-6 text-center text-muted">暂无可选会诊医生</div>
      ) : (
        <div className="space-y-4">
          <DialogField label="会诊医生">
            <SimpleSelect
              value={consultDoctorId}
              onChange={setConsultDoctorId}
              options={consultDoctorOptions}
              placeholder="请选择一位会诊医生"
            />
          </DialogField>
          <DialogField label="紧急程度">
            <RadioGroup
              aria-label="紧急程度"
              value={consultUrgency}
              onChange={(value) => setConsultUrgency(value as ConsultUrgency)}
              orientation="horizontal"
            >
              {(['urgent', 'normal', 'routine'] as ConsultUrgency[]).map(
                (urgency) => (
                  <Radio key={urgency} value={urgency}>
                    <Radio.Content>
                      <Radio.Control>
                        <Radio.Indicator />
                      </Radio.Control>
                      <Chip
                        color={consultUrgencyMap[urgency].theme}
                        variant="soft"
                      >
                        {consultUrgencyMap[urgency].text}
                      </Chip>
                    </Radio.Content>
                  </Radio>
                ),
              )}
            </RadioGroup>
          </DialogField>
          <DialogField label={coordinating ? '转交原因' : '会诊原因'}>
            <SimpleTextArea
              value={consultReason}
              onChange={setConsultReason}
              placeholder={
                coordinating
                  ? '请说明协调转交的原因和需要专科医生关注的事项'
                  : '请说明本次会诊的原因 / 关注点 / 期待'
              }
              maxLength={500}
              rows={3}
            />
          </DialogField>
        </div>
      )}
    </AppModal>
  );
}

function DialogField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-2 text-[13px] font-bold">{label}</div>
      {children}
    </label>
  );
}
